"use client"
import { type CSSProperties, useEffect, useRef, useState } from "react"
import { type LucideIcon } from "lucide-react"
import { LayoutGroup } from "motion/react"
import Tooltip from "@/ui/Tooltip"
import { useHudContext } from "../../shared/playbookHUDInfra"
import { type PolicyStatus } from "./policiesData"
import { useGroupedPoliciesForActiveDate } from "./useGroupedPoliciesForActiveDate"
import PoliciesPanelTooltipContent from "./PoliciesPanelTooltipContent"
import { usePolicyHoverSwap } from "./policyHoverSwap"
import { useMetricHoverSwap } from "../metrics/metricHoverSwap"
import { usePowerHoverSwap } from "../power/powerHoverSwap"
import { PoliciesStatusColumns } from "./policies-panel/PoliciesStatusColumns"
import {
  POLICY_ITEM_APPEARANCE,
  resolvePolicyStatusAppearances,
  type PolicyItemAppearance,
} from "./policies-panel/policyAppearance"

// Clearing the hovered policy is delayed a beat so the pointer crossing the
// gap between adjacent boxes doesn't flicker the single-policy card (shown
// below the always-visible full-list tooltip) out and back in.
const HOVER_CLEAR_DELAY_MS = 100

type PoliciesPanelProps = {
  className?: string
  compact?: boolean
  reverse?: boolean
  // "columns" (default): statuses side-by-side. "rows": statuses stacked
  // vertically, each labelled on the left — fits beside another HUD block.
  layout?: "columns" | "rows"
  strokeColor?: string
  // Overrides the status-icon color independently of strokeColor.
  iconColor?: string
  strokeStyle?: "solid" | "dashed"
  backgroundColor?: string
  borderRadius?: number
  gap?: number
  strokeWidth?: number
  squareSize?: number
  // Narrows each policy box's WIDTH only, keeping its height at squareSize/
  // BOX_SIZE — makes the icons read smaller without losing height.
  boxWidth?: number
  // Overrides the per-status label text (defaults to POLICY_STATUS_LABELS).
  // Use shorter labels so more boxes fit in a "rows"-layout column.
  statusLabels?: Partial<Record<PolicyStatus, string>>
  // Overrides the "rows" layout label-gutter width (default 96px).
  labelWidth?: number
  // Box outline shape forwarded to PolicyItem ("rounded" default, or "hexagon").
  shape?: "rounded" | "hexagon"
  // When true, boxes wrap onto multiple lines instead of overflowing — keeps a
  // crowded status from running off the edge in a narrow column.
  wrapBoxes?: boolean
  // "rows" layout label placement: "left" gutter (default), "top", or "right"
  // (boxes hug the column's left edge, label to their right). See e.g. HUD 74.
  rowsLabelPosition?: "left" | "top" | "right"
  // "rows" + left label: drop the fixed gutter so each label hugs its word and the
  // boxes start just after it, fitting more boxes per line. See e.g. HUD 59.
  labelHug?: boolean
  // In labelHug mode: align boxes to the row "start" (default — boxes hug the
  // label) or "end" (boxes right-align to the column edge). See e.g. HUD 68.
  labelHugAlign?: "start" | "end"
  // Per-status glyph that replaces the text label (lets the gutter shrink right
  // down). Keyed by status. See e.g. HUD 57.
  statusIcons?: Partial<Record<PolicyStatus, LucideIcon>>
  // When false, the status labels keep their given casing instead of the default
  // ALL-CAPS (so a variant can show e.g. "Current Policy" mixed-case).
  labelUppercase?: boolean
  // Overrides the status label font size (default 9px), e.g. "8.5px".
  labelFontSize?: string
  // In labelHug mode, overrides the gap (px) between the label word and the first
  // box (default 10). See e.g. HUD 75, which tightens it to 6.
  labelHugGapPx?: number
  // When a status row has too many boxes, the right-aligned overflow slides left
  // OVER the label — staying inside the column — instead of pushing past the
  // column's right edge. Pair with labelHugGapPx={0}. See HUD 86.
  overlapLabel?: boolean
  // Per-status SHORTER label swapped in only when that row's full label + boxes
  // would overflow the column (measured live). Rows that fit keep the full
  // label. Pair with overlapLabel to also keep any residual overflow inside the
  // column. See HUD 87.
  crowdedStatusLabels?: Partial<Record<PolicyStatus, string>>
  // Instead of swapping to a fallback, drop exactly the label characters the
  // boxes would touch or hide (e.g. "Current Policy" → "Current P"), revealing
  // them character by character as boxes move to other statuses. Pair with
  // overlapLabel. See HUD 88.
  truncateLabelOnOverlap?: boolean
  // Collapse the three statuses into TWO categories: enacted ("current") vs.
  // not-yet-a-policy ("in_discussion" + "off_the_table" merged under the
  // "off_the_table" key). See e.g. HUD 62.
  twoCategory?: boolean
  // Static CSS fill (e.g. a dither dot-screen) applied to each policy box in
  // place of the solid `backgroundColor`. Pass { backgroundImage, backgroundSize }.
  fillPatternStyle?: CSSProperties
  // Per-status static CSS fill, overriding `fillPatternStyle` for that column.
  // Use this to give each status its own dither density (e.g. denser for
  // "current", sparser for "off_the_table").
  fillPatternStyleByStatus?: Partial<Record<PolicyStatus, CSSProperties>>
  // Per-status appearance (fill / border / icon), replacing the default
  // POLICY_ITEM_APPEARANCE. Use this to give each status its own treatment
  // (e.g. POLICY_FILL_VS_OUTLINE_APPEARANCE) instead of one uniform override.
  statusAppearance?: Record<PolicyStatus, PolicyItemAppearance>
  // Drop the status glyph inside each box so it reads as a plain square — for
  // tiny boxes sized to the labor squares, where the icon won't fit. See HUD 78.
  hideIcon?: boolean
}

export const PoliciesPanel = ({
  className,
  compact = false,
  reverse = false,
  layout = "columns",
  strokeColor,
  iconColor,
  strokeStyle,
  backgroundColor,
  borderRadius,
  gap,
  strokeWidth,
  squareSize,
  boxWidth,
  statusLabels,
  labelWidth,
  shape,
  wrapBoxes,
  rowsLabelPosition,
  labelHug,
  labelHugAlign,
  statusIcons,
  labelUppercase,
  labelFontSize,
  labelHugGapPx,
  overlapLabel,
  crowdedStatusLabels,
  truncateLabelOnOverlap,
  twoCategory,
  fillPatternStyle,
  fillPatternStyleByStatus,
  statusAppearance,
  hideIcon,
}: PoliciesPanelProps) => {
  const { isPolicyGraphHidden } = useHudContext()
  const grouped = useGroupedPoliciesForActiveDate()
  const [hoveredPolicyId, setHoveredPolicyId] = useState<string | null>(null)
  const clearHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleHoverPolicy = (policyId: string | null) => {
    if (clearHoverTimeoutRef.current) clearTimeout(clearHoverTimeoutRef.current)
    if (policyId === null) {
      clearHoverTimeoutRef.current = setTimeout(() => setHoveredPolicyId(null), HOVER_CLEAR_DELAY_MS)
    } else {
      setHoveredPolicyId(policyId)
    }
  }
  useEffect(() => () => {
    if (clearHoverTimeoutRef.current) clearTimeout(clearHoverTimeoutRef.current)
  }, [])

  // Inside a <PolicyHoverSwap> provider the floating tooltip is suppressed:
  // hover is reported to the swap context instead, and a PolicyHoverSwapTarget
  // elsewhere on the HUD renders the hovered policy's detail in-canvas.
  // Entering a square also dismisses any hovered metric or treemap hover —
  // the detail families share one slot (see hoverSwapStickyZone.tsx and
  // belowHudDetailSwap.tsx).
  const hoverSwap = usePolicyHoverSwap()
  const metricSwap = useMetricHoverSwap()
  const powerSwap = usePowerHoverSwap()
  const reportHover = (policyId: string | null) => {
    if (hoverSwap) {
      // Squares only SET the takeover — the enclosing HoverSwapStickyZone
      // clears it when the pointer leaves the whole row, so PolicyItem's
      // per-square mouse-leave null is ignored here.
      if (policyId === null) return
      hoverSwap.setHoveredPolicyId(policyId)
      hoverSwap.setHoveredStatus(null)
      metricSwap?.setHoveredMetric(null)
      powerSwap?.setHoveredItem(null)
      return
    }
    handleHoverPolicy(policyId)
  }
  // Hovering a status LABEL (not a square) reports the status — the
  // below-the-HUD detail slot lists every policy by section while it's set.
  // Enter-only, like the other families; the sticky zone clears on exit.
  // Tabs 23–27 (labelHoverDisabled): the labels are not hover targets at all.
  const reportStatusLabelHover = hoverSwap && !hoverSwap.labelHoverDisabled
    ? (status: PolicyStatus) => {
        hoverSwap.setHoveredStatus(status)
        hoverSwap.setHoveredPolicyId(null)
        metricSwap?.setHoveredMetric(null)
        powerSwap?.setHoveredItem(null)
      }
    : undefined

  // The tooltip's row boxes mirror the panel boxes, so hand it the same
  // resolved appearances the columns render with.
  const resolvedStatusAppearance = resolvePolicyStatusAppearances(
    statusAppearance ?? POLICY_ITEM_APPEARANCE,
    { strokeColor, iconColor, strokeStyle, backgroundColor },
  )

  const columns = (
    <PoliciesStatusColumns
      grouped={grouped}
      onHoverPolicy={reportHover}
      onHoverStatusLabel={reportStatusLabelHover}
      compact={compact}
      reverse={reverse}
      layout={layout}
      strokeColor={strokeColor}
      iconColor={iconColor}
      strokeStyle={strokeStyle}
      backgroundColor={backgroundColor}
      borderRadius={borderRadius}
      gap={gap}
      strokeWidth={strokeWidth}
      squareSize={squareSize}
      boxWidth={boxWidth}
      statusLabels={statusLabels}
      labelWidth={labelWidth}
      shape={shape}
      wrapBoxes={wrapBoxes}
      rowsLabelPosition={rowsLabelPosition}
      labelHug={labelHug}
      labelHugAlign={labelHugAlign}
      statusIcons={statusIcons}
      labelUppercase={labelUppercase}
      labelFontSize={labelFontSize}
      labelHugGapPx={labelHugGapPx}
      overlapLabel={overlapLabel}
      crowdedStatusLabels={crowdedStatusLabels}
      truncateLabelOnOverlap={truncateLabelOnOverlap}
      twoCategory={twoCategory}
      fillPatternStyle={fillPatternStyle}
      fillPatternStyleByStatus={fillPatternStyleByStatus}
      statusAppearance={statusAppearance}
      hideIcon={hideIcon}
    />
  )

  if (isPolicyGraphHidden) return null
  // onMouseLeave lives on the outer wrapper so it survives Tooltip cloning
  // handlers onto its child. In swap mode the panel does NOT clear on leave —
  // the enclosing HoverSwapStickyZone clears when the pointer exits the whole
  // left column, so crossing the gap below the panel doesn't flash the treemap.
  return (
    <div
      className={className}
      onMouseLeave={hoverSwap ? undefined : () => handleHoverPolicy(null)}
    >
      <LayoutGroup>
        {hoverSwap ? columns : (
          // left-end: the tooltip's bottom edge aligns with the panel's bottom
          // edge (the "Off the Table" row), so the hovered-policy card can hang
          // below it top-aligned with the panel's bottom (see
          // PoliciesPanelTooltipContent). When the viewport is too short,
          // Tooltip's flip (bestFit) re-aligns and shift keeps it on screen.
          <Tooltip
            placement="left-end"
            maxWidth={560}
            disableShadow
            content={(
              <PoliciesPanelTooltipContent
                grouped={grouped}
                hoveredPolicyId={hoveredPolicyId}
                statusAppearance={resolvedStatusAppearance}
                borderRadius={borderRadius}
                strokeWidth={strokeWidth}
              />
            )}
          >
            {columns}
          </Tooltip>
        )}
      </LayoutGroup>
    </div>
  )
}
